import React from "react";
import { Alert, Box, Card, CardContent, Chip, Skeleton, Typography } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";

import { type FleetActions, BUCKET_HELP, BUCKET_COLOR } from "./fleetActionsFormat";

interface FleetActionsBandProps {
  actions: FleetActions | null;
  reason?: string | null;
  loading: boolean;
  isError: boolean;
  onRetry: () => void;
  onSelectBucket: (bucket: string) => void;
  /** Explicit "stamp baseline" action — the ONLY way the delta baseline advances.
   *  The GET is a pure read (reviewer F2: loads must not write). */
  onStampBaseline?: () => void;
}

/**
 * Fleet actions — the summary half. Bucket counts and, above them, the only line
 * that shows PROGRESS rather than restating the board: what recovered, what
 * regressed, and the net.
 *
 * Detail lives behind a click, deliberately: a 200-row list on the dashboard is a
 * list nobody reads, and the whole point of this panel is that it gets read.
 */
const FleetActionsBand: React.FC<FleetActionsBandProps> = ({
  actions,
  reason,
  loading,
  isError,
  onRetry,
  onSelectBucket,
  onStampBaseline,
}) => {
  if (isError) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Alert severity="warning" action={<Chip label="Retry" size="small" onClick={onRetry} />}>
            Couldn't read fleet actions.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Skeleton width={220} height={22} />
          <Skeleton width="60%" height={30} sx={{ mt: 1.5 }} />
          <Skeleton width="85%" height={40} sx={{ mt: 1.5 }} />
        </CardContent>
      </Card>
    );
  }

  // A null payload is NOT an empty panel. The route distinguishes "nothing is broken"
  // from "the query is wrong" and hands back a reason; showing that sentence is the
  // whole point — silence that reads as success is this system's known failure mode.
  if (!actions) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary" fontWeight={700}>
            Fleet actions
          </Typography>
          <Alert severity="info" sx={{ mt: 1 }}>
            {reason || "No fleet action data available."}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const { counts, bucket_order: order, delta, total } = actions;
  const net = delta.net;
  const TrendIcon = net > 0 ? TrendingUpIcon : net < 0 ? TrendingDownIcon : TrendingFlatIcon;
  const trendColor = net > 0 ? "success.main" : net < 0 ? "error.main" : "text.secondary";
  const freeWins = counts["RE-RUN"] || 0;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary" fontWeight={700}>
          Fleet actions — what to do next
        </Typography>

        {/* PROGRESS. Without this the panel is a snapshot, and a snapshot repeated
            every morning is indistinguishable from one that never changes. */}
        <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
          {delta.available ? (
            <>
              <TrendIcon sx={{ color: trendColor }} />
              <Typography variant="h6" fontWeight={700} sx={{ color: trendColor }}>
                {net > 0 ? `+${net}` : net}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                since the last run · {delta.recovered.length} recovered ·{" "}
                {delta.regressed.length} regressed · {delta.still_stuck} still stuck
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              First run — no baseline yet. Viewing this panel records nothing; stamp a
              baseline to start measuring progress.
            </Typography>
          )}
          {onStampBaseline && (
            <Chip
              label="Stamp baseline"
              size="small"
              variant="outlined"
              onClick={onStampBaseline}
              title="Record today's verdicts as the comparison point. Progress (recovered / regressed / net) is measured against the last stamp — page loads never move it."
            />
          )}
        </Box>

        {delta.available && net === 0 && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Net zero is itself a signal: nothing moved, so whatever is being worked isn't
            landing yet.
          </Typography>
        )}

        <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
          {order.map((bucket) => {
            const count = counts[bucket] || 0;
            if (!count) return null;
            return (
              <Chip
                key={bucket}
                label={`${bucket} ${count}`}
                title={BUCKET_HELP[bucket]}
                onClick={() => onSelectBucket(bucket)}
                variant={bucket === "OK" ? "outlined" : "filled"}
                color={BUCKET_COLOR[bucket]}
                sx={{ fontWeight: 700 }}
              />
            );
          })}
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
          {total} properties classified from diagnoses the scraper already computes · click a
          bucket for the per-property detail
          {freeWins > 0 && (
            <>
              {" · "}
              <Box component="span" sx={{ fontWeight: 700, color: "success.main" }}>
                {freeWins} need nothing but a re-run
              </Box>
            </>
          )}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default FleetActionsBand;
